import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Compass, ShieldCheck } from "lucide-react";
import RadarSweep from "@/components/RadarSweep";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  // Redirect back to /app after auth — AppRouter detects `session_id` in the
  // URL fragment and swaps to <AuthCallback /> synchronously on first render.
  const redirectUrl = `${window.location.origin}/app`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  return (
    <main
      data-testid="login-page"
      className="relative min-h-screen overflow-hidden bg-obsidian"
    >
      <div className="pointer-events-none absolute inset-0 hud-grid radial-fade opacity-30" />
      <div className="pointer-events-none absolute inset-x-0 top-1/3 h-[380px] bg-[radial-gradient(ellipse_at_center,rgba(46,119,136,0.14),transparent_70%)]" />

      <div className="relative z-10 mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:px-10">
        <Link to="/" data-testid="login-brand" className="flex items-center gap-2.5">
          <span className="relative flex h-6 w-6 items-center justify-center border border-teal/70">
            <Compass className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            <span className="absolute -top-px -left-px h-1 w-1 bg-amber" />
          </span>
          <span className="font-display text-lg text-ink">Life OS</span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-mist hover:text-ink"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
          Back to landing
        </Link>
      </div>

      <div className="relative z-10 mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl grid-cols-1 items-center gap-10 px-6 pb-16 md:grid-cols-12 md:px-10">
        <div className="hidden md:col-span-6 md:block">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
            Restricted airspace
          </p>
          <h1 className="mt-6 font-display text-5xl leading-[1.02] text-ink md:text-6xl">
            Sign in to
            <br />
            <span className="italic text-mist">the tower.</span>
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-mist">
            Life OS is invitation-only during preview. Sign in with the Google
            account you registered on the waitlist to enter your household&apos;s
            tower.
          </p>

          <div className="relative mt-10 max-w-sm">
            <span className="absolute -top-2 -left-2 h-4 w-4 border-t border-l border-teal/70" />
            <span className="absolute -bottom-2 -right-2 h-4 w-4 border-b border-r border-teal/70" />
            <div className="border border-rule bg-deep/60 p-6 backdrop-blur-sm">
              <RadarSweep />
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative md:col-span-6"
        >
          <div className="relative mx-auto max-w-md">
            <span className="absolute -top-2 -left-2 h-4 w-4 border-t border-l border-teal/70" />
            <span className="absolute -top-2 -right-2 h-4 w-4 border-t border-r border-teal/70" />
            <span className="absolute -bottom-2 -left-2 h-4 w-4 border-b border-l border-teal/70" />
            <span className="absolute -bottom-2 -right-2 h-4 w-4 border-b border-r border-teal/70" />

            <div className="border border-rule bg-deep/80 p-8 backdrop-blur-md md:p-10">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-teal">
                <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
                Sign-in
              </div>
              <h2 className="mt-4 font-display text-3xl text-ink">
                Enter Life OS
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-mist">
                Google sign-in only for now. We use it to identify you — never
                to nudge, track, or advertise.
              </p>

              <button
                onClick={startGoogleLogin}
                data-testid="google-signin-btn"
                className="mt-8 flex w-full items-center justify-center gap-3 border border-rule bg-obsidian px-6 py-4 font-mono text-[12px] uppercase tracking-[0.24em] text-ink transition-colors duration-300 hover:border-teal hover:bg-deep"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M17.64 9.204c0-.638-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.616z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 0 1 3.68 9c0-.593.102-1.17.284-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>

              <p className="mt-8 border-t border-rule pt-5 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Not invited yet?{" "}
                <a
                  href="/#waitlist"
                  data-testid="login-waitlist-link"
                  className="text-teal hover:text-teal-hover"
                >
                  Join the manifest →
                </a>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
